import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { Button } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import {
  ClientProfileSection,
  ClientProfileItem,
} from '../client-profile.model';

export const SECTION_EDIT_DIALOG_DATA = 'SECTION_EDIT_DIALOG_DATA' as const;

export interface SectionEditDialogData {
  section: ClientProfileSection;
  /** Current values keyed by itemEditKey(sectionId, item) */
  initialValues: Record<string, string | number>;
  /** When provided, Save calls this and waits for completion; button shows saving state. */
  save$?: (values: Record<string, string | number>) => Observable<unknown>;
}

export type SectionEditDialogResult =
  | { cancelled: true }
  | { cancelled: false; values: Record<string, string | number> };

function itemEditKey(sectionId: string, item: ClientProfileItem): string {
  return `${sectionId ?? 'section'}-${item.key ?? item.label}`;
}

@Component({
  selector: 'bai-client-profile-section-edit-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, Button, InputTextModule],
  templateUrl: './client-profile-section-edit-dialog.component.html',
  styleUrl: './client-profile-section-edit-dialog.component.scss',
})
export class ClientProfileSectionEditDialogComponent {
  private _ref = inject(DynamicDialogRef);
  private _config = inject<DynamicDialogConfig>(DynamicDialogConfig);

  private _data = this._config.data as SectionEditDialogData;
  readonly section = this._data.section;
  readonly sectionId = this.section.id ?? this.section.title;

  /** Local form values keyed by itemEditKey (editable items only). */
  readonly formValues = signal<Record<string, string>>({});

  /** True while a save request is in progress. */
  readonly saving = signal(false);

  /** When set, show inline message that no changes were made (do not call save). */
  readonly noChangesMessage = signal<string | null>(null);

  constructor() {
    const initial: Record<string, string> = {};
    for (const item of this.section.items) {
      if (item.editable) {
        const key = itemEditKey(this.sectionId, item);
        const v = this._data.initialValues[key] ?? item.value;
        initial[key] = v != null ? String(v) : '';
      }
    }
    this.formValues.set(initial);
  }

  itemEditKey(item: ClientProfileItem): string {
    return itemEditKey(this.sectionId, item);
  }

  editableItems(): ClientProfileItem[] {
    return this.section.items.filter((i) => i.editable);
  }

  getValue(key: string): string {
    return this.formValues()[key] ?? '';
  }

  setValue(key: string, value: string): void {
    this.noChangesMessage.set(null);
    this.formValues.update((m) => ({ ...m, [key]: value }));
  }

  /** True if current form values match initial values (no edits to save). */
  private hasNoChanges(values: Record<string, string | number>): boolean {
    const initial = this._data.initialValues;
    const keys = new Set([...Object.keys(values), ...Object.keys(initial)]);
    for (const key of keys) {
      const a = values[key];
      const b = initial[key];
      const aNorm = a == null || a === '' ? '' : String(a);
      const bNorm = b == null || b === '' ? '' : String(b);
      if (aNorm !== bNorm) return false;
    }
    return true;
  }

  cancel(): void {
    this._ref.close({ cancelled: true } as SectionEditDialogResult);
  }

  save(): void {
    this.noChangesMessage.set(null);
    const values: Record<string, string | number> = {};
    const raw = this.formValues();
    for (const item of this.editableItems()) {
      const key = this.itemEditKey(item);
      const v = raw[key];
      if (v !== undefined) {
        values[key] = typeof item.value === 'number' ? Number(v) : v;
      }
    }

    if (this.hasNoChanges(values)) {
      this.noChangesMessage.set('No changes were made to be saved.');
      return;
    }

    const save$ = this._data.save$;
    if (save$) {
      this.saving.set(true);
      save$(values).subscribe({
        next: () => {
          this.saving.set(false);
          this._ref.close({ cancelled: false, values } as SectionEditDialogResult);
        },
        error: () => {
          this.saving.set(false);
        },
      });
    } else {
      this._ref.close({ cancelled: false, values } as SectionEditDialogResult);
    }
  }
}
